import api from "./api";
import { httpErrorMessage } from "../utils/httpErrorMessage.js";

function normalizePayload(payload = {}) {
  const {
    clientEmail,
    projectTier,
    subscriptionPlan,
    projectId,
    websiteType,
    product,
    amount,
    currency = "RON",
    renewalDate,
    status = "inactive",
    autoRenew = false,
    projectImage,
    scope = false,
    projectUrl,
  } = payload;

  return {
    clientEmail: clientEmail?.trim(),
    projectTier,
    subscriptionPlan,
    projectId,
    websiteType,
    product,
    amount: Number(amount),
    currency: String(currency).toUpperCase(),
    renewalDate,
    status: String(status).toLowerCase(),
    autoRenew: Boolean(autoRenew),
    projectImage,
    scope: Boolean(scope),
    projectUrl,
  };
}

export async function createSubscriptionPlan(data) {
  if (data.planId && data.paymentMethodId) {
    return createSubscriptionWithStripe(data);
  }

  const payload = normalizePayload(data);
  try {
    const res = await api.post("/subscription-plans", payload);
    return { success: true, data: res.data };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to create subscription plan."),
    };
  }
}

export async function createSubscriptionWithStripe(data) {
  const payload = {
    planId: data.planId,
    paymentMethodId: data.paymentMethodId,
    customerEmail: data.customerEmail,
    customerName: data.customerName,
    productName: data.productName,
    projectTier: data.projectTier,
    subscriptionPlan: data.subscriptionPlan || data.projectTier,
    cardHolderName: data.cardHolderName,
    email: data.email,
    address: data.address,
    city: data.city,
    state: data.state,
    country: data.country,
    postalCode: data.postalCode,
    phone: data.phone,
    clientID: data.clientID,
    fullName: data.fullName,
    emailAddress: data.emailAddress,
    billingAddress: data.billingAddress,
    stateRegion: data.stateRegion,
    websiteType: data.websiteType || "business",
    projectUrl: data.projectUrl,
    amount: data.amount,
    currency: data.currency,
    status: data.status,
    autoRenew: data.autoRenew,
    isUpdating: data.isUpdating,
    existingPlanId: data.existingPlanId,
    existingProjectId: data.existingProjectId,
    existingStripeSubscriptionId: data.existingStripeSubscriptionId,
  };

  try {
    const res = await api.post("/subscription-plans/subscribe", payload);
    return { success: true, data: res.data };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to create subscription."),
    };
  }
}

export async function cancelSubscriptionPlan(data) {
  const payload =
    typeof data === "string"
      ? {
          planId: data,
          reason: "User requested cancellation",
          feedback: "",
        }
      : {
          planId: data.planId,
          reason: data.reason || "User requested cancellation",
          feedback: data.feedback || "",
        };

  try {
    const res = await api.post("/subscription-plans/cancel", payload);
    return { success: true, data: res.data };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to cancel subscription."),
    };
  }
}

export async function getSubscriptionPlans(params = {}) {
  try {
    const res = await api.get("/subscription-plans", { params });
    return { success: true, data: res.data };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to get subscription plans."),
    };
  }
}

export async function getUserSubscriptionPlans(params = {}) {
  try {
    const res = await api.get("/subscription-plans/my-plans", { params });
    return res.data;
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to get user subscription plans."),
    };
  }
}

export async function getSubscriptionPlanById(id) {
  try {
    const res = await api.get(`/subscription-plans/${id}`);
    return { success: true, data: res.data };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to get subscription plan."),
    };
  }
}

export async function updateSubscriptionPlan(id, data) {
  const payload = normalizePayload(data);
  try {
    const res = await api.put(`/subscription-plans/${id}`, payload);
    return { success: true, data: res.data };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to update subscription plan."),
    };
  }
}

export async function deleteSubscriptionPlan(id) {
  try {
    const res = await api.delete(`/subscription-plans/${id}`);
    return { success: true, data: res.data };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to delete subscription plan."),
    };
  }
}

export async function uploadSubscriptionPlanImage(
  imageFile,
  websiteName,
  imageType = "main"
) {
  try {
    const formData = new FormData();
    formData.append("projectImage", imageFile);
    formData.append("websiteName", websiteName || "subscription-project");
    formData.append("imageType", imageType);

    const response = await api.post("/subscription-plans/upload-image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      (error.response?.status === 404
        ? "Upload route not found. Please check server configuration."
        : "Failed to upload subscription plan image");

    throw new Error(errorMessage);
  }
}
